import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import { useCurrency } from '../context/CurrencyContext'
import { portfolioAPI, subscriptionsAPI } from '../lib/api'
import { Button } from '../components/ui/button'
import { Wallet, TrendingUp, Building2, Coins, ArrowRight, Clock, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  allocated: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
}

export function InvestorDashboard() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, isAuthenticated, isKYCApproved } = useAuth()
  const { formatPrice } = useCurrency()
  const [portfolio, setPortfolio] = useState(null)
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/'); return }
    const loadData = async () => {
      try {
        const [portfolioRes, subsRes] = await Promise.all([portfolioAPI.getSummary(), subscriptionsAPI.list()])
        setPortfolio(portfolioRes.data)
        setSubscriptions(Array.isArray(subsRes.data) ? subsRes.data : subsRes.data?.subscriptions || [])
      } catch (error) { console.error('Failed to load dashboard:', error) }
      finally { setLoading(false) }
    }
    loadData()
  }, [isAuthenticated, navigate])

  if (loading) return (<div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex items-center justify-center pt-32"><Loader2 className="w-8 h-8 animate-spin text-brand-primary" /></div></div>)

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl font-heading text-brand-secondary">{t('dashboard.investor.title')}</motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-slate-500 mt-1">{t('dashboard.investor.welcome')} {user?.name}</motion.p>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Button onClick={() => navigate('/marketplace')} className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="explore-properties-btn">{t('dashboard.investor.exploreProperties')} <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </motion.div>
          </div>

          {!isKYCApproved && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6 p-4 bg-amber-50 border border-amber-200">
              <div className="flex items-start gap-3"><AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" /><div>
                <p className="text-amber-700 font-semibold">{t('dashboard.investor.completeKyc')}</p>
                <p className="text-sm text-amber-600 mt-1">{t('dashboard.investor.kycNeeded')}</p>
                <Button onClick={() => navigate('/kyc')} size="sm" className="mt-3 h-10 bg-amber-500 hover:bg-amber-600 text-white rounded-none" data-testid="complete-kyc-btn">{t('dashboard.investor.completeKyc')}</Button>
              </div></div>
            </motion.div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: t('dashboard.investor.totalInvested'), value: formatPrice(portfolio?.total_invested || 0), icon: Wallet, color: 'text-brand-primary' },
              { label: t('dashboard.investor.currentValue'), value: formatPrice(portfolio?.current_value || 0), icon: TrendingUp, color: 'text-emerald-600' },
              { label: t('dashboard.investor.totalTokens'), value: portfolio?.total_tokens?.toLocaleString() || '0', icon: Coins, color: 'text-blue-600' },
              { label: t('dashboard.investor.properties'), value: portfolio?.properties_count || '0', icon: Building2, color: 'text-slate-600' },
            ].map((stat, i) => (
              <div key={i} className="bg-white border border-slate-100 p-5">
                <div className="flex items-center justify-between mb-3"><span className="text-slate-500 text-sm uppercase tracking-wider">{stat.label}</span><stat.icon className={`w-5 h-5 ${stat.color}`} /></div>
                <p className="text-2xl font-heading text-brand-secondary font-mono">{stat.value}</p>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white border border-slate-100">
            <div className="p-5 border-b border-slate-100"><h2 className="text-lg font-heading text-brand-secondary">{t('dashboard.investor.yourHoldings')}</h2></div>
            {portfolio?.holdings && portfolio.holdings.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {portfolio.holdings.map((holding) => (
                  <Link key={holding.subscription_id} to={`/property/${holding.property_id}`} className="flex items-center justify-between p-5 hover:bg-[#F8FAFC] transition-colors" data-testid={`holding-${holding.property_id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-brand-primary/10 flex items-center justify-center"><Building2 className="w-6 h-6 text-brand-primary" /></div>
                      <div><p className="text-brand-secondary font-semibold">{holding.property_name}</p><p className="text-sm text-slate-500">{holding.tokens_held} {holding.token_symbol || 'tokens'}</p></div>
                    </div>
                    <div className="text-right">
                      <p className="text-brand-secondary font-mono font-semibold">{formatPrice(holding.current_value)}</p>
                      <p className="text-sm text-emerald-600">+{holding.expected_yield}% {t('common.apy')}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center"><Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500 mb-2">{t('dashboard.investor.noHoldings')}</p><p className="text-sm text-slate-400 mb-6">{t('dashboard.investor.startInvesting')}</p>
                <Button onClick={() => navigate('/marketplace')} variant="outline" className="border-slate-200 text-brand-secondary hover:bg-slate-50 rounded-none">{t('dashboard.investor.browseProperties')}</Button>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 bg-white border border-slate-100">
            <div className="p-5 border-b border-slate-100"><h2 className="text-lg font-heading text-brand-secondary">{t('dashboard.investor.recentTransactions')}</h2></div>
            {Array.isArray(subscriptions) && subscriptions.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {subscriptions.slice(0, 5).map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-5" data-testid={`subscription-${sub.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 flex items-center justify-center ${sub.status === 'allocated' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                        {sub.status === 'allocated' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Clock className="w-5 h-5 text-amber-600" />}
                      </div>
                      <div><p className="text-brand-secondary font-semibold">Investment #{sub.payment_reference}</p><p className="text-sm text-slate-400">{new Date(sub.created_at).toLocaleDateString()}</p></div>
                    </div>
                    <div className="text-right">
                      <p className="text-brand-secondary font-mono font-semibold">{formatPrice(sub.amount)}</p>
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${statusColors[sub.status]}`}>{sub.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center"><Clock className="w-12 h-12 mx-auto text-slate-300 mb-4" /><p className="text-slate-500">{t('dashboard.investor.noTransactions')}</p></div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  )
}
